import React from 'react';
import { Alert } from 'reactstrap';

import { formatDateString } from '~libs/dayjs';

import { Spinner } from '~components';
import { Switch } from '~components/form-fields/inputs';

import { SahCategory } from '~lookups/models';
import { COPY_STATUS, Section, SectionItem } from '~support-at-home/models';

/** A contribution category change a copy ran into: which services, out of where, into where, when. */
export interface CategoryMoveGroup {
  key: string;
  serviceNames: string[];
  fromCategoryName: string;
  toCategoryName: string;
  changeDate: string | null;
}

/** The shape the bulk paths report a moved line in. */
export interface MovedServiceLike {
  service_type_id: string;
  service_type_name: string | null;
  from_category_name: string | null;
  to_category_name: string | null;
  change_date: string | null;
}

interface RawMove {
  serviceName?: string;
  from: string;
  to: string;
  changeDate: string | null;
}

/**
 * Collapse moved lines into one group per category change.
 *
 * One change moving three personal care services is one thing that has happened. Naming it once -
 * the way the cutover banner on Service Type Associations does - reads as the mandated change a
 * provider already knows about, where three near identical lines read as three separate problems.
 */
const groupMoves = (moves: RawMove[]): CategoryMoveGroup[] => {
  const groups = new Map<string, CategoryMoveGroup>();

  for (const move of moves) {
    const key = `${move.from}:${move.to}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        serviceNames: [],
        fromCategoryName: move.from,
        toCategoryName: move.to,
        changeDate: move.changeDate,
      });
    }

    const existing = groups.get(key) as CategoryMoveGroup;
    if (move.serviceName && !existing.serviceNames.includes(move.serviceName)) {
      existing.serviceNames.push(move.serviceName);
    }
  }

  return [...groups.values()];
};

/**
 * The category changes in a single copy's reconciled sections.
 *
 * Keyed on the move data, not the copy status. A declined move comes back as an ordinary removal -
 * which is what it is, once nobody is carrying it across - so a status test would make the notice
 * vanish the moment it was switched off, taking the switch with it.
 */
export const groupMovesFromSections = (
  sections: Section[],
  resolveServiceName: (serviceTypeId: string) => string | undefined,
): CategoryMoveGroup[] =>
  groupMoves(
    (sections ?? []).flatMap((section) =>
      ((section.items ?? []) as SectionItem[])
        .filter((item) => item.moved_to_category || item.copy_status === COPY_STATUS.CATEGORY_MOVED)
        .map((item) => ({
          serviceName: resolveServiceName(item.service_type_id),
          from: (item.moved_from_category as SahCategory | undefined)?.name ?? 'its previous category',
          // The section a moved line sits in IS its destination, so the section's own category is
          // the fallback when the line did not carry one.
          to: (item.moved_to_category as SahCategory | undefined)?.name ?? section.category?.name ?? 'a new category',
          changeDate: item.moved_on ?? null,
        })),
    ),
  );

/** The category changes across a bulk run's per-budget rows. */
export const groupMovesFromMovedServices = (movedServices: MovedServiceLike[]): CategoryMoveGroup[] =>
  groupMoves(
    (movedServices ?? []).map((moved) => ({
      serviceName: moved.service_type_name ?? undefined,
      from: moved.from_category_name ?? 'its previous category',
      to: moved.to_category_name ?? 'a new category',
      changeDate: moved.change_date,
    })),
  );

/** The single copy's own hint, where the sections it names are directly below the notice. */
const DEFAULT_APPLIED_HINT =
  'check the participant contribution on the sections below, or leave them out if you would rather add them yourself.';

export interface ComponentProps {
  groups: CategoryMoveGroup[];
  applyCategoryMoves: boolean;
  onToggle: (applyCategoryMoves: boolean) => void;
  /** What the affected services sit on - the one budget being copied, or the several a run covers. */
  scopeLabel?: string;
  /**
   * What to go and check, once the services have been carried across. The single copy can point at
   * the sections right below the notice; a bulk run has only per-budget counts on screen, and telling
   * a provider to check sections that are not there reads as a missing screen.
   */
  appliedHint?: string;
  /**
   * A reconcile is in flight for this choice. Says so beside the control rather than leaving the
   * budget below silently stale: re-reading it replaces every section, and until it lands the
   * sections on screen are the answer to the PREVIOUS position of the switch.
   */
  isLoading?: boolean;
  disabled?: boolean;
  dataCy?: string;
}

/**
 * The services a copy has carried into a different contribution category, and the choice to leave
 * them behind instead.
 *
 * A copy across the date a service changes category used to drop the line and say the service was no
 * longer on the target price plan - which was untrue, since the service is still on the plan, and
 * unactionable, since the funding the budget had allocated to it simply went missing.
 *
 * Worded to match the cutover banner on Service Type Associations, which states the same change from
 * the other end: services move from one category to another on a date. A provider meeting this on a
 * copy has usually already read that one, and two different accounts of one change read as two
 * changes. Single and bulk copy share this component for that same reason - it is one change and one
 * decision, and only how many budgets it lands on differs.
 *
 * The move is offered, not imposed. It is the right answer often enough to be the default - the
 * funding is preserved unless someone chooses otherwise - but a provider may have a reason to leave
 * a service behind, and before this they had no way to say so that did not involve rebuilding the
 * budget by hand.
 */
const CategoryMoveNotice: React.FC<ComponentProps> = ({
  groups,
  applyCategoryMoves,
  onToggle,
  scopeLabel = 'this budget',
  appliedHint = DEFAULT_APPLIED_HINT,
  isLoading = false,
  disabled = false,
  dataCy,
}) => {
  if (!groups || groups.length === 0) return null;

  const affectedCount = groups.reduce((total, entry) => total + entry.serviceNames.length, 0);

  return (
    <Alert
      color="warning"
      className="d-flex justify-content-between align-items-start"
      data-cy={dataCy ?? 'category-move-notice'}
    >
      <div className="pe-3">
        {groups.map((entry) => (
          <b key={entry.key} className="d-block">
            {entry.serviceNames.length > 0 ? entry.serviceNames.join(', ') : 'These services'} move from{' '}
            {entry.fromCategoryName} to {entry.toCategoryName}
            {entry.changeDate ? ` on ${formatDateString(entry.changeDate)}` : ''}.
          </b>
        ))}
        <div>
          {affectedCount === 1 ? 'One service on' : `${affectedCount} services on`} {scopeLabel}{' '}
          {affectedCount === 1 ? 'is' : 'are'} affected.{' '}
          {applyCategoryMoves
            ? `They have been carried into the category that applies - ${appliedHint}`
            : 'They are being left out of the copy, along with the funding allocated to them.'}
        </div>
      </div>
      {/* The caption belongs to the LEFT of the switch, which is the reverse of how the shared Switch
          lays itself out. Switch takes no className, so the flip is done with a modifier class on a
          wrapper rather than a prop. */}
      <div className="sah-category-move-switch d-flex align-items-center gap-2">
        {isLoading && <Spinner loading type="beat" size={6} dataCy="category-move-spinner" />}
        <Switch
          id="apply_category_moves"
          type="switch"
          name="apply_category_moves"
          value={applyCategoryMoves}
          disabled={disabled}
          labels={['Remove', 'Move']}
          onChange={(_name, next) => onToggle(Boolean(next))}
        />
      </div>
    </Alert>
  );
};

export default CategoryMoveNotice;
